import { useMutation, useQueryClient } from '@tanstack/react-query';

import { loadFromStorage, saveToStorage } from '@/utils/client-storage/localStorage';
import { saveToSessionStorage } from '@/utils/client-storage/sessionStorage';
import { getAndStoreVersion, getCookie, setCookie, triggerDiscordMessage } from '@/utils/functions';
import secureApi from './secureApi';
import { claimUpsert as handleClaimUpsert } from './useClaimUpsert';
import { DISCORD_BOTS } from './constants';
import { updateClaimOnUserVisit } from '@/components/UserVisitTracker/utils';

interface GenerateSessionPayload {
  utmTags: string;
}

export const generateSession = async ({ utmTags }: GenerateSessionPayload) => {
  const existingClaimId = loadFromStorage('currentId') || getCookie('currentId');
  const url = existingClaimId
    ? `/session/claim/${encodeURIComponent(existingClaimId)}`
    : '/session/claim';

  try {
    const response = await secureApi(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      _retry: true,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      const error = new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      (error as any).response = { status: response.status, data: errorData };
      throw error;
    }

    const responseData = await response.json();
    const claimId = responseData?.responseObject?.claimId;
    const session_duration = responseData?.responseObject?.data?.data?.session_duration;

    if (!claimId) {
      console.error('Claim ID is not available in the response.');
      console.error('Claim ID not found during session generation.');
      throw new Error('Claim ID not available.');
    }

    const utmTagsJSON = JSON.parse(utmTags);
    let channel = 'Default';
    const channelCases: { [key: string]: string } = {
      Sub2_FairInc: 'Sub2',
      outbrain: 'Outbrain',
      reclaims: 'Reclaims',
      jasonppc: 'Jason PPC',
      infinity: 'Infinity Leads',
      justizia: 'Justizia',
      check_FairInc: 'check',
    };

    try {
      Object.keys(utmTagsJSON || {}).forEach((tagKey) => {
        if (tagKey === 'utm_campaign') {
          Object.keys(channelCases).forEach((channelCase) => {
            if (utmTagsJSON[tagKey].includes(channelCase)) {
              channel = channelCases[channelCase];
            }
          });
        }
        if (tagKey === 'utm_source') {
          const utmSource = utmTagsJSON[tagKey];
          saveToStorage('utm_source', utmSource);
          setCookie('utm_source', utmSource);
        }
        saveToStorage(tagKey, utmTagsJSON[tagKey]);
        setCookie(tagKey, utmTagsJSON[tagKey]);
      });
    } catch (e) {
      console.error('Error setting channel:', e);
    }

    saveToStorage('currentId', claimId);
    try {
      saveToSessionStorage('currentId', claimId);
    } catch (err) {
      console.error('Error saving session storage:', err);
    }
    try {
      setCookie('currentId', claimId, 90);
    } catch (err) {
      console.error("Error setting cookie 'currentId':", err);
    }

    if (session_duration) {
      saveToStorage('sessionStart', (Date.now() - session_duration).toString());
    } else {
      saveToStorage('sessionStart', Date.now().toString());
    }

    const queryString = new URL(window.location.href).search;
    const params = new URLSearchParams(queryString);
    const fbclid = params.get('fbclid') || getCookie('fbclid') || '';
    const ttclid = params.get('ttclid') || getCookie('ttclid') || getCookie('_ttclid') || '';
    const fbp = getCookie('_fbp') || '';
    const fbc = getCookie('_fbc') || '';
    const ttp = getCookie('_ttp') || '';
    const journey = getAndStoreVersion();
    const startFromFlag = loadFromStorage('START_FROM') || '';

    const initSessionPayload: { [key: string]: any } = {
      claimNumber: claimId,
      channel,
      fbclid,
      ttclid,
      fbp,
      fbc,
      ttp,
      journey,
      utmParams: utmTagsJSON,
      ...utmTagsJSON,
    };

    if (!startFromFlag && startFromFlag === '') {
      const ab_test_value = 'STARTED FROM LANDING_PAGE_V9';
      initSessionPayload.ab_test_value = ab_test_value;
    }

    // Update claim number for current user visit tracking
    updateClaimOnUserVisit(claimId);

    await secureApi('/session/claim', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(initSessionPayload),
    });

    import('@fingerprintjs/botd')
      .then((m) => m.load())
      .then((botd) => botd.detect())
      .then((result) => {
        const { bot = false } = result;
        if (bot) {
          handleClaimUpsert({
            claimNumber: claimId,
            is_bot: true,
            bot_config: {
              ...result,
            },
          });
          triggerDiscordMessage({
            bot: DISCORD_BOTS.BOT_ALERT,
            content: `# Claim Number: **${claimId}**
                      - Bot detected by BotD:\n\`\`\`${JSON.stringify(result)}\`\`\``,
          });
          // window.location.href = '/404';
        }
      })
      .catch((error) => console.error(error));

    console.info('Generated session');
    handleClaimUpsert({ claimNumber: claimId, initSessionPayload });
    return { claimId, initSessionPayload };
  } catch (error: any) {
    console.error('Unable to generate session:', error);
    if (!(error instanceof Error) || !error.message.includes('Operation not allowed')) {
      console.error(error?.message || 'Error generating session!');
    }
    throw error;
  }
};

export const useGenerateSession = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { claimId: string; initSessionPayload: any },
    Error,
    GenerateSessionPayload,
    unknown
  >({
    mutationFn: generateSession,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['session', data.claimId] });
    },
  });
};
